import { loadStripe, Stripe } from "@stripe/stripe-js";
import { useRouter } from "next/router";
import React, { createContext, useContext, ReactNode, useMemo } from "react";
import { useOrganization } from "~hooks";

interface StripeConfigContextType {
  integrationId?: string;
  publishableKey?: string;
  connectedAccountId?: string;
  expressCheckoutEnabled?: boolean;
  stripePromise?: Promise<Stripe> | undefined;
}

const StripeConfigContext = createContext<StripeConfigContextType | undefined>(
  undefined
);

interface StripeProviderProps extends StripeConfigContextType {
  children: ReactNode;
  config: StripeConfigContextType;
}

export const StripeConfigProvider: React.FC<StripeProviderProps> = ({
  config = {},
  children,
}) => {
  const expressCheckoutEnabled = useExpressCheckoutEnabled(config);

  const stripePromise = useMemo(async () => {
    if (config?.publishableKey) {
      return loadStripe(config.publishableKey, {
        stripeAccount: config.connectedAccountId ?? undefined, // This property does not accept `null`
      });
    }
    return null;
  }, [config?.publishableKey, config?.connectedAccountId]);

  return (
    <StripeConfigContext.Provider
      value={{
        ...config,
        expressCheckoutEnabled,
        stripePromise,
      }}
    >
      {children}
    </StripeConfigContext.Provider>
  );
};

export const useStripeConfigContext = () => {
  const context = useContext(StripeConfigContext);
  if (!context) {
    console.error(
      "useStripeConfigContext must be used within a StripeProvider"
    );
  }
  return context;
};

function useExpressCheckoutEnabled(config: StripeConfigContextType) {
  const { hasFeature } = useOrganization();
  // Express checkout is only enabled for customers if the organization has a Stripe connect account
  // Legacy stripe accounts are used for internal demoing purposes only via "legacyStripeExpressCheckout"
  const allowStripeAccount =
    (!!config?.connectedAccountId && config.expressCheckoutEnabled) ||
    hasFeature("StripeExpressCheckoutLegacy");

  const expressCheckoutEnabled =
    allowStripeAccount &&
    !!config?.publishableKey;

  return expressCheckoutEnabled;
}
